// Floor Plan PDF export - extracted from AdminFloorPlanBuilder.js to keep that
// component under ~700 lines and let this module be unit-tested independently.
//
// Pure function: takes all dependencies as parameters, has no React state.
// IMPORTANT: any change here must match the on-screen canvas output 1:1 (we had
// a coordinate-shift bug in symbol overlay before - see comments below).
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import jsPDF from 'jspdf';
import { SYMBOL_LIBRARY, SYMBOL_CATEGORIES } from './FloorPlanSymbols';

// Pre-render an uploaded background image into a NEW canvas that matches the
// on-screen DOM container's aspect ratio EXACTLY, applying proper
// `object-fit: contain` math (centered letterbox / pillarbox with white
// background). This is required because html2canvas v1 does NOT correctly
// render `object-fit: contain` on inner <img> tags - it falls back to "fill"
// which stretches the image, but pins remain in the original 4:3 coordinate
// space => symbols visually drift off their drop points in the exported PDF.
// By baking the letterbox into the image itself, html2canvas only needs to
// stretch a 4:3 image into a 4:3 container, which is a no-op visually.
const createLetterboxedDataUrl = (imageUrl, targetAspect) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // Use a high-res base width to preserve detail on the PDF page.
        const W = 2000;
        const H = Math.round(W * targetAspect);
        const c = document.createElement('canvas');
        c.width = W; c.height = H;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, W, H);
        const imgW = img.naturalWidth || img.width;
        const imgH = img.naturalHeight || img.height;
        const containerAspect = H / W;          // target h/w
        const imgAspect = imgH / imgW;           // source h/w
        let drawW, drawH;
        if (imgAspect > containerAspect) {
          // image is "taller" relative to container -> fit by height
          drawH = H;
          drawW = H / imgAspect;
        } else {
          drawW = W;
          drawH = W * imgAspect;
        }
        const offsetX = (W - drawW) / 2;
        const offsetY = (H - drawH) / 2;
        ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
        resolve(c.toDataURL('image/jpeg', 0.92));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('Failed to load background image'));
    img.src = imageUrl;
  });

// Convert a symbol SVG to a transparent PNG data URL for embedding in the PDF.
// Rotation is baked into the PNG so jsPDF doesn't have to rotate (jsPDF's
// rotation pivots around the top-left, which would shift pins off their drop
// point).
export const symbolToPngDataUrl = (symbolKey, sizePx = 160, rotationDeg = 0) =>
  new Promise((resolve, reject) => {
    try {
      const def = SYMBOL_LIBRARY[symbolKey];
      if (!def) { reject(new Error('Symbol not found: ' + symbolKey)); return; }
      const C = def.Component;
      const innerMarkup = ReactDOMServer.renderToStaticMarkup(<C />);
      const svgString =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="${sizePx}" height="${sizePx}">` +
        innerMarkup +
        `</svg>`;
      const encoded = encodeURIComponent(svgString)
        .replace(/'/g, '%27').replace(/"/g, '%22');
      const dataUri = `data:image/svg+xml;charset=utf-8,${encoded}`;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = sizePx; c.height = sizePx;
        const ctx = c.getContext('2d');
        if (rotationDeg) {
          ctx.translate(sizePx / 2, sizePx / 2);
          ctx.rotate((rotationDeg * Math.PI) / 180);
          ctx.drawImage(img, -sizePx / 2, -sizePx / 2, sizePx, sizePx);
        } else {
          ctx.drawImage(img, 0, 0, sizePx, sizePx);
        }
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('SVG rasterize failed: ' + symbolKey));
      img.src = dataUri;
    } catch (err) { reject(err); }
  });

/**
 * Generate the 3-page BELLAJA floor plan PDF (Cover + Legend + Cost).
 *
 * @param {Object} p
 * @param {{current:HTMLElement}} p.canvasRef    DOM ref of the on-screen canvas
 * @param {Object} p.client                       Form data
 * @param {string} p.propertyType                 e.g. 'residential' | 'commercial'
 * @param {Array}  p.pins                         Placed symbol pins
 * @param {Object} p.symbolCounts                 { symbolKey: count }
 * @param {Array}  p.annotations                  Text / shape annotations
 * @param {Object} p.estimate                     AI summary object (optional)
 * @param {Object} p.totals                       { sub, tx, co, total }
 * @param {Function} p.priceFor                   (symbolKey) => unit price
 * @param {number} p.taxRate
 * @param {number} p.taxBase
 * @param {number} p.contingencyRate
 * @param {boolean} [p.returnBlob=false]
 * @returns {Promise<null|{blob:Blob, filename:string}>}
 */
export async function generateFloorPlanPdf({
  canvasRef, client, propertyType, pins, symbolCounts, annotations = [],
  estimate, totals, priceFor, taxRate, taxBase, contingencyRate,
  returnBlob = false,
}) {
  if (!canvasRef?.current) return null;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 12;

  // ---- Page 1: Two-column header (Company LEFT, Client RIGHT) + Floor Plan ----
  pdf.setFillColor(10, 10, 10);
  pdf.rect(0, 0, pageW, 8, 'F');

  const colWidth = (pageW - margin * 3) / 2;
  const leftX = margin;
  const rightX = margin + colWidth + margin;
  let ly = 16;
  pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22);
  pdf.text('BELLAJA', leftX, ly);
  ly += 5;
  pdf.setTextColor(60, 60, 60); pdf.setFontSize(8); pdf.setFont('helvetica', 'normal');
  pdf.text('Electrical & Security Systems', leftX, ly);
  ly += 6;
  pdf.setDrawColor(227, 27, 35); pdf.setLineWidth(0.4);
  pdf.line(leftX, ly, leftX + colWidth, ly);
  ly += 5;
  pdf.setTextColor(50, 50, 50); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
  pdf.text('PHONE', leftX, ly); pdf.setFont('helvetica', 'normal'); pdf.text('(203) 803-7035', leftX + 18, ly); ly += 4.5;
  pdf.setFont('helvetica', 'bold'); pdf.text('EMAIL', leftX, ly); pdf.setFont('helvetica', 'normal'); pdf.text('info@bellajaelectric.com', leftX + 18, ly); ly += 4.5;
  pdf.setFont('helvetica', 'bold'); pdf.text('WEB', leftX, ly); pdf.setFont('helvetica', 'normal'); pdf.text('www.bellajaelectric.com', leftX + 18, ly); ly += 4.5;
  pdf.setFont('helvetica', 'bold'); pdf.text('ADDRESS', leftX, ly); pdf.setFont('helvetica', 'normal');
  const addrLines = pdf.splitTextToSize('Serving NYC, Connecticut, NJ - 30 mile radius', colWidth - 18);
  pdf.text(addrLines, leftX + 18, ly); ly += addrLines.length * 4 + 1;
  pdf.setFont('helvetica', 'italic'); pdf.setTextColor(120, 120, 120); pdf.setFontSize(7);
  pdf.text('NYC DOB Licensed  |  CT State Licensed  |  Fully Insured & Bonded', leftX, ly);

  // RIGHT column - Project / Client details
  let ry = 16;
  pdf.setTextColor(10, 10, 10); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
  pdf.text('NEW PROJECT DETAILS', rightX, ry);
  ry += 4;
  pdf.setDrawColor(10, 10, 10); pdf.setLineWidth(0.3);
  pdf.line(rightX, ry, rightX + colWidth, ry); ry += 5;
  pdf.setTextColor(50, 50, 50); pdf.setFontSize(8);
  const rrow = (k, v) => {
    pdf.setFont('helvetica', 'bold'); pdf.text(k, rightX, ry);
    pdf.setFont('helvetica', 'normal');
    const lines = pdf.splitTextToSize(String(v || '-'), colWidth - 28);
    pdf.text(lines, rightX + 26, ry);
    ry += lines.length * 4 + 0.5;
  };
  rrow('PROJECT', client.project_name);
  rrow('CLIENT', `${client.first_name} ${client.last_name}`);
  rrow('PHONE', client.phone);
  rrow('EMAIL', client.email);
  rrow('COMPANY', client.company || '-');
  rrow('ADDRESS', client.address);
  rrow('TYPE', propertyType.toUpperCase());
  rrow('DATE', new Date().toLocaleDateString());

  // Title for floor plan
  let y = Math.max(ly, ry) + 6;
  pdf.setDrawColor(227, 27, 35); pdf.setLineWidth(0.5);
  pdf.line(margin, y, pageW - margin, y); y += 4;
  pdf.setTextColor(10, 10, 10); pdf.setFontSize(11); pdf.setFont('helvetica', 'bold');
  pdf.text('PROFESSIONAL FLOOR PLAN', pageW / 2, y, { align: 'center' });
  y += 3;

  // Floor plan image - capture only the background image and annotations
  const html2canvas = (await import('html2canvas')).default;

  const cRect = canvasRef.current.getBoundingClientRect();
  const captureW = Math.round(cRect.width);
  const captureH = Math.round(cRect.height);
  const domAspect = captureH / captureW;

  // BUG FIX: html2canvas does not honor `object-fit: contain` on inner <img>
  // tags - it stretches them to fill, which causes pin coordinates (which are
  // % of the 4:3 container) to drift relative to the rendered floor plan in
  // the exported PDF. We work around this by cloning the canvas off-screen,
  // pre-baking the letterbox into a new image (so the contained image is
  // already at the container's aspect ratio), and running html2canvas on the
  // clone. This keeps pin overlays mathematically aligned with the floor plan.
  const bgImgEl = canvasRef.current.querySelector('img');
  let cloneEl = null;
  let captureTarget = canvasRef.current;

  if (bgImgEl && bgImgEl.src) {
    try {
      const letterboxed = await createLetterboxedDataUrl(bgImgEl.src, domAspect);
      cloneEl = canvasRef.current.cloneNode(true);
      cloneEl.style.position = 'fixed';
      cloneEl.style.top = '-100000px';
      cloneEl.style.left = '-100000px';
      cloneEl.style.width = captureW + 'px';
      cloneEl.style.height = captureH + 'px';
      cloneEl.style.aspectRatio = 'auto';
      cloneEl.style.pointerEvents = 'none';
      document.body.appendChild(cloneEl);

      const clonedImg = cloneEl.querySelector('img');
      if (clonedImg) {
        // The letterboxed image already matches the container aspect ratio,
        // so swap object-contain to object-fill (no-op) to avoid html2canvas
        // misinterpreting the property and to be explicit about intent.
        clonedImg.style.objectFit = 'fill';
        clonedImg.src = letterboxed;
        await new Promise((resolve) => {
          if (clonedImg.complete && clonedImg.naturalWidth > 0) return resolve();
          clonedImg.onload = resolve;
          clonedImg.onerror = resolve;
        });
      }
      // Hide pins in the clone so they don't get baked twice into the
      // background image.
      cloneEl.querySelectorAll('[data-fp-pin]').forEach(n => {
        n.style.visibility = 'hidden';
      });
      captureTarget = cloneEl;
    } catch (e) {
      // If pre-rendering fails (e.g. CORS), fall back to the original element.
      // eslint-disable-next-line no-console
      console.warn('Floor plan letterbox pre-render failed; falling back', e);
      cloneEl = null;
      captureTarget = canvasRef.current;
    }
  }

  // For the fallback path (no clone), still hide pins on the live element.
  let livePinNodes = [];
  if (captureTarget === canvasRef.current) {
    livePinNodes = Array.from(canvasRef.current.querySelectorAll('[data-fp-pin]'));
    livePinNodes.forEach(n => { n.style.visibility = 'hidden'; });
  }

  const cnv = await html2canvas(captureTarget, {
    backgroundColor: '#ffffff', scale: 2, useCORS: true, allowTaint: true,
    width: captureW, height: captureH, windowWidth: captureW, windowHeight: captureH,
  });

  if (cloneEl) {
    document.body.removeChild(cloneEl);
  } else {
    livePinNodes.forEach(n => { n.style.visibility = 'visible'; });
  }
  const imgData = cnv.toDataURL('image/jpeg', 0.92);

  // Derive PDF placement aspect from the live DOM rect (not html2canvas output)
  // so pin overlay coordinates remain consistent with what the user saw.
  const maxPlanH = pageH - y - 16;
  let finalW = pageW - margin * 2;
  let finalH = finalW * domAspect;
  if (finalH > maxPlanH) {
    finalH = maxPlanH;
    finalW = finalH / domAspect;
  }
  const planX = (pageW - finalW) / 2;
  const planY = y + 4;
  pdf.addImage(imgData, 'JPEG', planX, planY, finalW, finalH);

  // Pre-render symbol PNGs once per (key, rotation) combo
  const symbolPngCache = {};
  const getPng = async (key, rot) => {
    const cacheKey = `${key}@${rot || 0}`;
    if (symbolPngCache[cacheKey] !== undefined) return symbolPngCache[cacheKey];
    try { symbolPngCache[cacheKey] = await symbolToPngDataUrl(key, 160, rot || 0); }
    catch { symbolPngCache[cacheKey] = null; }
    return symbolPngCache[cacheKey];
  };

  // Overlay pins at their EXACT proportional positions
  for (const p of pins) {
    const png = await getPng(p.symbolKey, p.rotation || 0);
    if (!png) continue;
    const symMm = 7 * (p.scale || 1);
    const xMm = planX + (p.x / 100) * finalW - symMm / 2;
    const yMm = planY + (p.y / 100) * finalH - symMm / 2;
    pdf.addImage(png, 'PNG', xMm, yMm, symMm, symMm);
  }

  // Pre-render unrotated PNGs for the legend (page 2)
  const uniqueKeys = Object.keys(symbolCounts);
  const symbolPngs = {};
  await Promise.all(uniqueKeys.map(async (k) => { symbolPngs[k] = await getPng(k, 0); }));

  pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
  pdf.text('Page 1 of 3  |  Cover + Floor Plan', pageW / 2, pageH - 5, { align: 'center' });

  // ---- Page 2: Legend ----
  pdf.addPage();
  pdf.setFillColor(10, 10, 10); pdf.rect(0, 0, pageW, 16, 'F');
  pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text('SYMBOL LEGEND', margin, 11);
  pdf.setTextColor(220, 220, 220); pdf.setFontSize(8);
  pdf.text(client.project_name, pageW - margin, 11, { align: 'right' });

  pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);

  let yl = 24;
  const rowH = 16;
  const colW = (pageW - margin * 2) / 2;
  let col = 0;
  uniqueKeys.forEach((key) => {
    const s = SYMBOL_LIBRARY[key];
    const qty = symbolCounts[key];
    const xc = margin + col * colW;

    pdf.setDrawColor(220, 220, 220); pdf.setLineWidth(0.2);
    pdf.rect(xc, yl - 5, 11, 11);
    if (symbolPngs[key]) {
      pdf.addImage(symbolPngs[key], 'PNG', xc + 0.5, yl - 4.5, 10, 10);
    }
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
    pdf.setTextColor(227, 27, 35);
    pdf.text(`x${qty}`, xc + 13, yl - 1);
    pdf.setTextColor(20, 20, 20);

    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
    pdf.text(s.name, xc + 22, yl - 1);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
    const desc = pdf.splitTextToSize(s.desc, colW - 26);
    pdf.text(desc, xc + 22, yl + 3);
    pdf.setFont('helvetica', 'italic'); pdf.setFontSize(7); pdf.setTextColor(150, 150, 150);
    pdf.text(`Category: ${SYMBOL_CATEGORIES[s.category].name}`, xc + 22, yl + 7);
    pdf.setTextColor(20, 20, 20);

    col = (col + 1) % 2;
    if (col === 0) yl += rowH;

    if (yl > pageH - 30) {
      pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
      pdf.text('Symbol Legend (continued)', pageW / 2, pageH - 5, { align: 'center' });
      pdf.addPage();
      pdf.setFillColor(10, 10, 10); pdf.rect(0, 0, pageW, 16, 'F');
      pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
      pdf.text('SYMBOL LEGEND (continued)', margin, 11);
      pdf.setTextColor(220, 220, 220); pdf.setFontSize(8);
      pdf.text(client.project_name, pageW - margin, 11, { align: 'right' });
      pdf.setTextColor(20, 20, 20);
      yl = 24; col = 0;
    }
  });
  if (col === 1) yl += rowH;

  if (annotations.length > 0) {
    yl += 4;
    pdf.setFont('helvetica', 'bold'); pdf.text('ANNOTATIONS', margin, yl); yl += 5;
    pdf.setFont('helvetica', 'normal');
    annotations.filter(a => a.type === 'text').forEach((a) => {
      pdf.text(`- ${a.text}`, margin + 2, yl); yl += 4.5;
    });
    const shapeCount = annotations.filter(a => a.type !== 'text').length;
    if (shapeCount) { pdf.text(`- ${shapeCount} measurement / shape annotation(s)`, margin + 2, yl); yl += 4.5; }
  }

  pdf.setFontSize(7); pdf.setTextColor(120, 120, 120);
  pdf.text('Page 2 of 3  |  Symbol Legend', pageW / 2, pageH - 5, { align: 'center' });

  // ---- Page 3: Cost Breakdown ----
  pdf.addPage();
  pdf.setFillColor(10, 10, 10); pdf.rect(0, 0, pageW, 16, 'F');
  pdf.setTextColor(227, 27, 35); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text('COST BREAKDOWN', margin, 11);
  pdf.setTextColor(220, 220, 220); pdf.setFontSize(8);
  pdf.text('Estimate (subject to final inspection)', pageW - margin, 11, { align: 'right' });

  pdf.setTextColor(20, 20, 20); pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
  let by = 24;
  pdf.text('Symbol', margin, by);
  pdf.text('Item', margin + 14, by);
  pdf.text('Qty', pageW - 60, by, { align: 'right' });
  pdf.text('Unit $', pageW - 38, by, { align: 'right' });
  pdf.text('Total $', pageW - margin, by, { align: 'right' });
  pdf.setDrawColor(220, 220, 220); pdf.line(margin, by + 2, pageW - margin, by + 2);
  by += 6;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);

  const { sub, tx, co, total } = totals;
  for (const [key, qty] of Object.entries(symbolCounts)) {
    const s = SYMBOL_LIBRARY[key];
    const unit = priceFor(key);
    const totalLine = unit * qty;
    if (symbolPngs[key]) {
      pdf.addImage(symbolPngs[key], 'PNG', margin, by - 4, 6, 6);
    }
    pdf.text(s.name, margin + 8, by);
    pdf.text(String(qty), pageW - 60, by, { align: 'right' });
    pdf.text(`$${unit.toFixed(2)}`, pageW - 38, by, { align: 'right' });
    pdf.text(`$${totalLine.toFixed(2)}`, pageW - margin, by, { align: 'right' });
    by += 7;
    if (by > pageH - 50) { pdf.addPage(); by = 24; }
  }

  by += 4;
  pdf.setDrawColor(180, 180, 180); pdf.line(margin, by, pageW - margin, by); by += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Subtotal', pageW - 60, by); pdf.text(`$${sub.toFixed(2)}`, pageW - margin, by, { align: 'right' }); by += 5;
  pdf.setFont('helvetica', 'normal');
  pdf.text(`NYC Tax (${(taxRate * 100).toFixed(3)}% on ${(taxBase * 100).toFixed(0)}% of subtotal)`, pageW - 60, by, { align: 'right' });
  pdf.text(`$${tx.toFixed(2)}`, pageW - margin, by, { align: 'right' }); by += 5;
  pdf.text(`Contingency (${(contingencyRate * 100).toFixed(1)}%)`, pageW - 60, by, { align: 'right' });
  pdf.text(`$${co.toFixed(2)}`, pageW - margin, by, { align: 'right' }); by += 6;
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
  pdf.setDrawColor(227, 27, 35); pdf.setLineWidth(0.6);
  pdf.line(margin, by - 3, pageW - margin, by - 3);
  pdf.text('GRAND TOTAL', pageW - 70, by + 3);
  pdf.setTextColor(227, 27, 35);
  pdf.text(`$${total.toFixed(2)}`, pageW - margin, by + 3, { align: 'right' });
  pdf.setTextColor(20, 20, 20);
  by += 12;

  if (estimate?.summary) {
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
    pdf.text('AI ANALYSIS', margin, by); by += 5;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    const summary = pdf.splitTextToSize(estimate.summary, pageW - margin * 2);
    pdf.text(summary, margin, by); by += summary.length * 4.5 + 3;
  }

  pdf.setFontSize(8); pdf.setTextColor(120, 120, 120);
  const terms = [
    'Terms: Prices are estimates only. Final pricing may vary based on site conditions, materials, and permits required.',
    'Warranty: 1-year labor warranty on all installed equipment.',
    'Payment: 50% deposit, balance on completion. We accept Cash, Check, Credit Card.',
    'License: NYC DOB Licensed | Connecticut State Licensed | Fully Insured & Bonded.'
  ];
  terms.forEach(t => { pdf.text(t, margin, by); by += 4; });
  pdf.text('Page 3 of 3  |  Cost Breakdown', pageW / 2, pageH - 5, { align: 'center' });

  const filename = `BELLAJA-FloorPlan-${client.project_name.replace(/\s+/g, '_')}.pdf`;
  if (returnBlob) {
    return { blob: pdf.output('blob'), filename };
  }
  pdf.save(filename);
  return null;
}
